import { useState, useCallback } from 'react';
import useSWR from 'swr';
import { 
  swrFetcher, 
  initiateTransfer, 
  getTransferStatus, 
  cancelTransfer 
} from '../services/apiClient';

// Hook for transfer history
export function useTransferHistory(params = {}, refreshInterval = 30000) {
  // Filter out undefined values before creating URLSearchParams
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([_, v]) => v !== undefined && v !== '')
  );
  const queryString = new URLSearchParams(cleanParams).toString();
  const url = `/transfers/history${queryString ? `?${queryString}` : ''}`;
  
  const { data, error, isLoading, mutate } = useSWR(
    url,
    swrFetcher,
    {
      refreshInterval,
      revalidateOnFocus: false,
    }
  );

  return {
    transfers: data?.transfers || [],
    pagination: data?.pagination,
    isLoading,
    isError: error,
    error,
    refresh: mutate,
  };
}

// Hook for a single transfer status (with polling)
export function useTransferStatus(transferId, pollInterval = 5000) {
  const { data, error, isLoading, mutate } = useSWR(
    transferId ? `/transfers/${transferId}` : null,
    swrFetcher,
    {
      refreshInterval: (data) => {
        // Stop polling if transfer is complete or failed
        if (data?.status === 'completed' || data?.status === 'failed' || data?.status === 'cancelled') {
          return 0;
        }
        return pollInterval;
      },
      revalidateOnFocus: false,
    }
  );

  return {
    transfer: data,
    isLoading,
    isError: error,
    error,
    refresh: mutate,
  };
}

// Hook for managing transfer operations
export function useTransferOperations() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  const submitTransfer = useCallback(async (transferData) => {
    setIsSubmitting(true);
    setError(null);
    
    try {
      const result = await initiateTransfer(transferData);
      setLastResult(result);
      return result;
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message;
      setError(errorMessage);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const checkStatus = useCallback(async (transferId) => {
    try {
      const result = await getTransferStatus(transferId);
      return result;
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message;
      setError(errorMessage);
      throw err;
    }
  }, []);

  const cancel = useCallback(async (transferId) => {
    setIsSubmitting(true);
    setError(null);
    
    try {
      const result = await cancelTransfer(transferId);
      return result;
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message;
      setError(errorMessage);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    submitTransfer,
    checkStatus,
    cancel,
    isSubmitting,
    error,
    lastResult,
    clearError,
  };
}

export default useTransferHistory;

